import Vue from 'vue'
import Vuex from 'vuex'
import utils from 'js/bankrate-utilities'
import { merge, forEach, some } from 'lodash'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    isMobile: utils.isMobile(),
    mortgagePayment: 0,
    loanAmount: 0,

    // Inputs
    wages: 0,
    investments: 0,
    alimonyIncome: 0,
    otherIncome: 0,
    downPayment: 0,
    loanTerm: 0,
    interestRate: 0,
    homeownerInsurance: 0,
    realEstateTaxes: 0,
    carPayment: 0,
    alimonyPaid: 0,
    creditCardPayment: 0,
    otherDebtPayment: 0,

    // Results
    partialLTV: 0,
    cumulativeLTV: 0,

    // Errors
    errors: {
      wages: false,
      investments: false,
      alimonyIncome: false,
      otherIncome: false,
      downPayment: false,
      loanTerm: false,
      interestRate: false,
      homeownerInsurance: false,
      realEstateTaxes: false,
      carPayment: false,
      alimonyPaid: false,
      creditCardPayment: false,
      otherDebtPayment: false
    },

    // Previous input
    previousUserInputs: {
      wages: 0,
      investments: 0,
      alimonyIncome: 0,
      otherIncome: 0,
      downPayment: 0,
      loanTerm: 0,
      interestRate: 0,
      homeownerInsurance: 0,
      realEstateTaxes: 0,
      carPayment: 0,
      alimonyPaid: 0,
      creditCardPayment: 0,
      otherDebtPayment: 0
    }
  },
  mutations: {
    updatePageState(state, payload) {
      state[payload.name] = payload.value
    },
    setPageErrors(state, payload) {
      let error = typeof payload.error === 'undefined' ? false : payload.error
      state.errors = merge(state.errors, { [payload.name]: error })
    },
    updatePreviousUserInputs(state) {
      forEach(state.previousUserInputs, (value, key) => {
        state.previousUserInputs[key] = state[key]
      })
    }
  },
  actions: {
    loadConfig({ state, commit }, config) {
      forEach(state, (value, key) => {
        if (typeof config[key] !== 'undefined' && config[key].default) {
          commit('updatePageState', { name: key, value: config[key].default })
        }
      })
    },
    setNumberValue({ commit }, payload) {
      commit('updatePageState', { name: payload.name, value: payload.value })
      commit('setPageErrors', { name: payload.name, error: payload.error })
    },
    calculate({ state, commit }) {
      commit('updatePreviousUserInputs')

      let mortgagePayment = 0
      let loanAmount = 0

      const totalMonthlyIncome =
        state.wages +
        state.investments +
        state.alimonyIncome +
        state.otherIncome
      const totalMonthlyExpenses =
        state.homeownerInsurance / 12 +
        state.realEstateTaxes / 12 +
        state.carPayment +
        state.alimonyPaid +
        state.creditCardPayment +
        state.otherDebtPayment
      const mortgagePaymentLowerBound = 0.28 * totalMonthlyIncome
      const mortgagePaymentBasedOnExpenses =
        0.36 * totalMonthlyIncome - totalMonthlyExpenses
      const monthlyInterestRate = state.interestRate / 1200
      const loanTermInMonths = 12 * state.loanTerm
      const rateCoefficient = Math.pow(
        1 + monthlyInterestRate,
        loanTermInMonths
      )

      if (mortgagePaymentLowerBound < mortgagePaymentBasedOnExpenses) {
        mortgagePayment = mortgagePaymentLowerBound
        loanAmount =
          ((mortgagePayment -
            state.homeownerInsurance / 12 -
            state.realEstateTaxes / 12) *
            (rateCoefficient - 1)) /
            (rateCoefficient * monthlyInterestRate) +
          state.downPayment
      } else {
        mortgagePayment = mortgagePaymentBasedOnExpenses
        loanAmount =
          (mortgagePayment * (rateCoefficient - 1)) /
            (rateCoefficient * monthlyInterestRate) +
          state.downPayment
      }

      commit('updatePageState', {
        name: 'mortgagePayment',
        value: mortgagePayment
      })
      commit('updatePageState', { name: 'loanAmount', value: loanAmount })
    }
  },
  getters: {
    pageHasErrors(state) {
      return some(state.errors, item => item)
    },
    dataChanged(state) {
      return some(state.previousUserInputs, (value, key) => {
        return value !== state[key]
      })
    },
    totalMonthlyIncome(state) {
      const income =
        state.wages +
        state.investments +
        state.alimonyIncome +
        state.otherIncome
      return typeof income === 'number' ? income : 0
    },
    totalMonthlyExpenses(state) {
      const expenses =
        state.homeownerInsurance / 12 +
        state.realEstateTaxes / 12 +
        state.carPayment +
        state.alimonyPaid +
        state.creditCardPayment +
        state.otherDebtPayment
      return typeof expenses === 'number' ? expenses : 0
    }
  }
})
